import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Instagram, Linkedin, Youtube, ArrowRight } from "lucide-react";
import founderCutout from "@/assets/images/founder-transparent-cropped.webp";
import founderSignature from "@/assets/images/gopi-signature.png";
import PortalCard from "@/components/PortalCard";
import Reveal from "@/components/Reveal";
import FadeIn from "@/components/FadeIn";
import SEO from "@/components/SEO";
import PlatformButton from "@/components/PlatformButton";
import NewsletterForm from "@/components/NewsletterForm";
import { VENTURES } from "@/data/ventures";
import { SOCIAL_LINKS } from "@/data/socialLinks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WHAT_WE_DO = [
  { title: "Private Real Estate Advisory", copy: "Curated guidance for discerning buyers, sellers and investors across residential, commercial and land opportunities." },
  { title: "Premium Interior Design", copy: "Conscious luxury interiors shaped through architecture, emotion and psychological intentionality." },
  { title: "Art, Music & Digital Creations", copy: "An enchanted creative world of digital art, healing music, meaningful books and imaginative tools." },
  { title: "Life Alchemy & Transformation", copy: "A sanctuary for reflection and inner alchemy, helping people move beyond fear and live deliberately." },
];

// Factual, non-fabricated figures derived directly from the group's real
// structure (never invented client counts, revenue or years of
// experience) -- fills the previously empty right-hand space in the
// Group Introduction section.
const GROUP_STATS = [
  { value: String(VENTURES.length), label: "Distinct Ventures" },
  { value: String(VENTURES.filter((v) => v.status === "live").length), label: "Live Digital Brands" },
  { value: "1", label: "Founder-Led Vision" },
  { value: "Ahmedabad", label: "Group Headquarters" },
];

const Home = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/blog`, { params: { limit: 3 } }).then((res) => setBlogPosts(res.data)).catch(() => setBlogPosts([]));
    axios.get(`${API}/gallery`, { params: { limit: 6 } }).then((res) => setGalleryItems(res.data)).catch(() => setGalleryItems([]));
    axios.get(`${API}/testimonials`, { params: { limit: 3 } }).then((res) => setTestimonials(res.data)).catch(() => setTestimonials([]));
  }, []);

  return (
    <div>
      <SEO
        title="Building Trust. Creating Legacy."
        description="Gopi Gadhvi Group is a founder-led house of ventures spanning private real estate advisory, interior design, creative arts, digital products and personal transformation."
        path="/"
      />

      <section className="hero-section-height relative flex items-center overflow-hidden px-6 md:px-0" data-testid="home-hero">
        <div
          className="page-shell w-full grid grid-cols-1 lg:grid-cols-[56%_44%] items-center pt-32 pb-12 lg:pt-32 lg:pb-16"
          style={{ gap: "48px", minHeight: "inherit" }}
        >
          <div className="relative z-10 text-left" style={{ maxWidth: "700px" }}>
            <span className="hero-eyebrow-ivory block mb-6" data-testid="home-hero-eyebrow">
              Real Estate · Design · Art · Life Alchemy
            </span>

            <Reveal as="h1" className="hero-tagline-script" delay={80}>
              A Universe of Its Own
            </Reveal>

            <p className="hero-paragraph-ivory mt-9 mb-[42px]" data-testid="home-hero-copy">
              Gopi Gadhvi Group is a founder-led house of ventures spanning luxury real estate,
              interior design, creative digital products and personal transformation, united by
              thoughtful creation, uncompromising integrity and a long-term vision.
            </p>

            <div className="flex flex-col min-[431px]:flex-row flex-wrap items-stretch min-[431px]:items-center gap-[18px] mb-[42px]">
              <Link to="/our-group" className="btn-glass-gold" data-testid="home-cta-explore-world">
                Explore Our World
              </Link>
              <Link to="/ventures" className="btn-glass-gold" data-testid="home-cta-explore-group">
                Explore Our Brands
              </Link>
              <Link to="/founder" className="btn-glass-gold" data-testid="home-cta-meet-founder">
                Meet the Founder
              </Link>
            </div>
          </div>

          <FadeIn className="hero-founder-wrap relative flex items-center justify-center" delay={200}>
            <img
              src={founderCutout}
              alt="Gopi Gadhvi, Founder & CEO of Gopi Gadhvi Group"
              className="hero-founder-image relative z-10 object-contain"
              data-testid="home-founder-hero-image"
            />
          </FadeIn>
        </div>
      </section>

      <section className="relative page-shell py-14 md:py-20 lg:py-24">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16">
          {/* Row 1 (desktop): heading+paragraphs vs. stats grid, vertically
              centered against EACH OTHER ONLY. The signature block lives in
              its own row 2 below (column 1 only) so it can never affect
              this row's height or the stats grid's centering, regardless
              of the signature's own size. */}
          <div className="editorial-col lg:col-start-1 lg:row-start-1 lg:self-center">
            <Reveal>
              <span className="eyebrow block mb-6">The House of Gopi Gadhvi</span>
              <h2 className="text-h2 mb-8">Different Worlds.<br />One Enduring Philosophy.</h2>
            </Reveal>
            <Reveal delay={100}>
              <p className="text-editorial text-justify mb-6">
                Gopi Gadhvi Group brings together ventures that may appear distinct, yet share one
                foundation: thoughtful creation, uncompromising integrity and a commitment to
                building lasting value.
              </p>
              <p className="text-editorial text-justify">
                From extraordinary properties and intentional interiors to art, music, books and
                inner transformation, every venture is an expression of one larger vision: to create
                work that changes how people live, feel, imagine and grow.
              </p>
            </Reveal>
          </div>

          {/* Row 2 (desktop): signature block, confined to column 1 only
              (same width as the text column above), so its height never
              participates in row 1's centering calculation below. Placed
              right after the text block in DOM order so mobile stacking
              (text + signature adjacent, then stats) matches the original
              flow exactly. */}
          <Reveal delay={150} className="lg:col-start-1 lg:row-start-2 mt-8 lg:mt-10">
            <img
              src={founderSignature}
              alt="Gopi Gadhvi signature"
              className="founder-signature-img mb-3"
              data-testid="home-founder-signature"
            />
            <p className="founder-name-text" style={{ color: "var(--gg-logo-gold)" }}>Gopi Gadhvi</p>
            <p className="founder-title-text">Founder &amp; CEO</p>
          </Reveal>

          <Reveal
            delay={140}
            className="grid grid-cols-2 gap-5 lg:col-start-2 lg:row-start-1 lg:self-center"
            data-testid="home-group-stats"
          >
            {GROUP_STATS.map((stat) => (
              <div key={stat.label} className="neu-surface neu-surface-hover p-7 text-center" data-testid={`home-stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="text-h3" style={{ color: "var(--gg-logo-gold)" }}>{stat.value}</p>
                <p className="text-small mt-2">{stat.label}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="relative page-shell py-14 md:py-20 lg:py-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Reveal>
            <span className="eyebrow block mb-5">Step Through</span>
            <h2 className="text-h2 mb-5">Our Brands</h2>
            <p className="text-editorial mx-auto">
              Every brand has its own world. Every world begins with the same philosophy.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="home-venture-grid">
          {VENTURES.map((v, i) => (
            <Reveal key={v.slug} delay={i * 90}>
              <PortalCard venture={v} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative py-14 md:py-20 lg:py-24" style={{ background: "var(--gg-midnight-black)" }}>
        <div className="page-shell">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Reveal>
              <span className="eyebrow block mb-5">What We Do</span>
              <h2 className="text-h2">Four Disciplines. One Standard of Craft.</h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {WHAT_WE_DO.map((item, i) => (
              <Reveal key={item.title} delay={i * 80} className="neu-surface neu-surface-hover p-7">
                <p className="eyebrow mb-3" style={{ color: "var(--gg-logo-gold)" }}>{String(i + 1).padStart(2, "0")}</p>
                <h3 className="text-h4 mb-3">{item.title}</h3>
                <p className="text-body">{item.copy}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center">
            <Link to="/contact" className="btn-solid-pearl" data-testid="home-cta-begin-conversation">
              Begin a Conversation <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {blogPosts.length > 0 && (
        <section className="relative page-shell py-14 md:py-20 lg:py-24" data-testid="home-blog-preview">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <span className="eyebrow block mb-5">From the Journal</span>
              <h2 className="text-h2">Latest Blog Posts</h2>
            </div>
            <Link to="/blog" className="btn-outline-silver btn-pill-sm" data-testid="home-blog-view-all">
              View All Posts
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {blogPosts.map((post, i) => (
              <Reveal key={post.id} delay={i * 80} className="h-full">
                <Link to={`/blog/${post.slug}`} className="neu-surface neu-surface-hover blog-card" data-testid={`home-blog-card-${post.slug}`}>
                  <div className="blog-card-image">
                    {post.cover_image && <img src={post.cover_image} alt={post.title} loading="lazy" />}
                  </div>
                  <div className="blog-card-body">
                    <span className="eyebrow" style={{ color: "var(--gg-logo-gold)" }}>{post.category}</span>
                    <h3 className="text-h4 mt-3 mb-2">{post.title}</h3>
                    <p className="text-body blog-card-excerpt">{post.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {galleryItems.length > 0 && (
        <section className="relative py-14 md:py-20 lg:py-24" style={{ background: "var(--gg-midnight-black)" }} data-testid="home-gallery-preview">
          <div className="page-shell">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <span className="eyebrow block mb-5">A Visual Journey</span>
                <h2 className="text-h2">Gallery Preview</h2>
              </div>
              <Link to="/gallery" className="btn-outline-silver btn-pill-sm" data-testid="home-gallery-view-all">
                View Gallery
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {galleryItems.map((item, i) => (
                <Reveal key={item.id} delay={i * 60} className="neu-surface gallery-thumb aspect-square">
                  <img src={item.image_url} alt={item.title} loading="lazy" />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {testimonials.length > 0 && (
        <section className="relative page-shell py-14 md:py-20 lg:py-24" data-testid="home-testimonials">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Reveal>
              <span className="eyebrow block mb-5">In Their Words</span>
              <h2 className="text-h2">Testimonials</h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {testimonials.map((t, i) => (
              <Reveal key={t.id} delay={i * 90} className="h-full">
                <div className="neu-panel neu-surface-hover testimonial-card h-full">
                  <span className="testimonial-quote-mark" aria-hidden="true">&ldquo;</span>
                  <p className="font-cormorant italic text-[19px] mb-6" style={{ color: "var(--gg-ivory)" }}>&ldquo;{t.quote}&rdquo;</p>
                  <p className="text-small font-semibold" style={{ color: "var(--gg-logo-gold)" }}>{t.name}</p>
                  {t.role_or_company && <p className="text-small">{t.role_or_company}</p>}
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="relative py-14 md:py-20 lg:py-24" style={{ background: "var(--gg-midnight-black)" }} data-testid="home-newsletter">
        <div className="page-shell max-w-2xl mx-auto text-center">
          <Reveal>
            <span className="eyebrow block mb-5">Stay Connected</span>
            <h2 className="text-h2 mb-6">Join Our Newsletter</h2>
            <p className="text-editorial mx-auto mb-10">
              Receive considered updates from Gopi Gadhvi Group and the brands you care about most.
            </p>
            <NewsletterForm />
          </Reveal>
        </div>
      </section>

      <section className="relative page-shell pt-16 md:pt-20 lg:pt-24 pb-20 text-center">
        <span className="eyebrow block mb-6">Follow Us</span>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <PlatformButton href={SOCIAL_LINKS.linkedin} label="LinkedIn" icon={Linkedin} testId="home-social-linkedin" />
          <PlatformButton href={SOCIAL_LINKS.instagram} label="Instagram" icon={Instagram} testId="home-social-instagram" />
          <PlatformButton href={SOCIAL_LINKS.youtube} label="YouTube" icon={Youtube} testId="home-social-youtube" />
        </div>
      </section>
    </div>
  );
};

export default Home;
